import React, { useCallback } from "react";
import { useSelector } from 'react-redux';
import { useDropzone } from "react-dropzone";
import { storage } from "../firebase_setup/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import {
  Pane,
  CloudUploadIcon,
  toaster,
  Paragraph,
  Link,
} from 'evergreen-ui';

function ImageUpload(props) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);

  // Improved slugify function to preserve more of the original filename
  const slugify = text => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^\w\s.-]/g, "") // Only remove truly problematic characters
      .replace(/[\s]+/g, "_") // Replace spaces with underscores
      .replace(/^-+|-+$/g, ""); // Trim leading/trailing hyphens
  }

  // Function to resize image before upload
  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calculate new dimensions (300px width, maintain aspect ratio)
          const MAX_WIDTH = 300;
          // Ensure that we don't try to upscale small images, cap at original width if smaller than MAX_WIDTH
          const targetWidth = Math.min(MAX_WIDTH, img.width);
          const scaleFactor = targetWidth / img.width;
          const height = img.height * scaleFactor;

          // Create canvas and resize image
          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetWidth, height);

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            // Create a new file with the same name but resized
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          }, file.type, 0.8); // 0.8 quality (80%)
        };
        img.onerror = (error) => {
          reject(error);
        };
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const imageDrop = async (file) => {
    try {
      toaster.notify("Uploading image...", { duration: 10 });

      // 1. Handle and upload the original image
      const originalFileName = file.name;
      const originalLastDotIndex = originalFileName.lastIndexOf('.');
      const originalExtension = originalLastDotIndex !== -1 ? originalFileName.slice(originalLastDotIndex + 1) : '';
      const originalFilenameWithoutExt = originalLastDotIndex !== -1 ? originalFileName.slice(0, originalLastDotIndex) : originalFileName;
      const originalSlugifiedName = slugify(originalFilenameWithoutExt);
      const finalOriginalName = originalExtension ? `${originalSlugifiedName}.${originalExtension}` : originalSlugifiedName;

      const originalStoragePath = `images/${currentpublisher.id}/${props.pathType}/${props.itemId}/${finalOriginalName}`;
      const originalStorageRef = ref(storage, originalStoragePath);

      toaster.notify("Uploading original image...", { id: 'original-upload', duration: 60 });
      const originalUploadResult = await uploadBytes(originalStorageRef, file);
      const originalDownloadURL = await getDownloadURL(originalUploadResult.ref);
      toaster.success("Original image uploaded.", { id: 'original-upload' });

      // 2. Resize the image for thumbnail
      toaster.notify("Processing thumbnail...", { id: 'thumb-process', duration: 60 });
      const resizedFile = await resizeImage(file); // Use the original file for resizing

      // Create a distinct name for the thumbnail
      const thumbFilenameWithoutExt = originalSlugifiedName + "_thumb"; // Add suffix
      const finalThumbName = originalExtension ? `${thumbFilenameWithoutExt}.${originalExtension}` : thumbFilenameWithoutExt;

      const thumbStoragePath = `images/${currentpublisher.id}/${props.pathType}/${props.itemId}/${finalThumbName}`;
      const thumbStorageRef = ref(storage, thumbStoragePath);

      // Upload thumbnail to Firebase
      toaster.notify("Uploading thumbnail...", { id: 'thumb-upload', duration: 60 });
      await uploadBytes(thumbStorageRef, resizedFile); // We don't strictly need the thumbDownloadURL here for the callback
      toaster.success("Thumbnail uploaded.", { id: 'thumb-upload' });

      // 3. Pass ONLY the original download URL to the parent component
      props.handleImageUpload(originalDownloadURL);

      toaster.closeAll(); // Close any remaining persistent notifications
      toaster.success("All image uploads complete!");

    } catch (error) {
      const errorCode = error.code || "unknown";
      const errorMessage = error.message || "Unknown error";
      toaster.closeAll();
      toaster.danger("Error uploading image. Try again or contact developer.");
      console.error("Upload error:", errorCode, errorMessage, error);
    }
  }

  const onDrop = useCallback(acceptedFiles => {
    // Do something with the files
    acceptedFiles.forEach(file => {
      let name = file.name.toLowerCase();
      if (
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".png")
      ) {
        imageDrop(file);
      } else {
        toaster.warning("You can only upload JPG, JPEG, and PNG image files.");
      }
    });
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  return (
    <Pane {...getRootProps()} className="file-uploader">
      <input {...getInputProps()} />
      {
        isDragActive ?
          <Pane size={400}><CloudUploadIcon
            size={24}
            marginRight={8}
            color={"var(--robinegg)"}
          />
          <Paragraph size={300}>{" "}Drop file here ...</Paragraph>
          </Pane> :
          <Pane display="flex" flexDirection="column" alignItems="center"><CloudUploadIcon
            size={24}
            color={"var(--robinegg)"}
          />
          <Paragraph size={400} color={"var(--darkgrey)"}>{" "}<Link>Browse</Link> or drag a file here</Paragraph>
          </Pane>
      }
    </Pane>
  )
}

export default ImageUpload;
